import { IcAlert, IcEye, IcFlightplan } from '@assets/icons/24x24';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import {
  getClearanceStatusIcon,
  getNotificationColor,
  getWorstNotificationLevel,
} from '@utils/data/notification.utils';
import { getReliabilityStatusIcon, getTacticalClearanceStatusIcon, getTargetReliability } from '@utils/target.utils';
import { FormattedMessage } from 'react-intl';

import { TacticalClearanceEnum, Target } from '@/types/c2/c2.types';
import { DroneNotificationLight } from '@/types/data/data.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { DataInfoSizeEnum } from '@/types/target.types';

import PopupContainer from '../../../common/PopupContainer';

type Props =
  | { target: Target<TargetLocation>; notification?: DroneNotificationLight }
  | { notification: DroneNotificationLight; target?: never };

export default function DroneInformation({ notification, target }: Readonly<Props>) {
  const reliability = target ? getTargetReliability(target) : notification.reliability;
  const hasFlightPlan = target ? target.hasOngoingFlightPlan : notification.hasFlightPlan;
  const clearance = target ? target.clearance : notification.clearance;
  const tacticalClearance = target?.tacticalClearance ?? TacticalClearanceEnum.NOTHING_SIGNIFICANT;
  const dataInfoIconSize = DataInfoSizeEnum.TARGET_DETAIL;

  return (
    <PopupContainer>
      <VStack gap="standardXS" width="100%">
        <HStack width="100%" gap={3}>
          <HStack gap={1} flex={1}>
            <Icon as={getTacticalClearanceStatusIcon(tacticalClearance, dataInfoIconSize)} />
            <Text textStyle="screenStd" fontWeight="medium">
              <FormattedMessage id={`target.tacticalClearance.${tacticalClearance}`} />
            </Text>
          </HStack>
          {notification ? (
            <HStack gap={1} flex={1}>
              <Icon as={IcAlert} boxSize={{ base: '20px', '4k': '24px' }} color={getNotificationColor(notification)} />
              {target === undefined && (
                <Text textStyle="screenStd" fontWeight="medium" color="neutral.500">
                  <FormattedMessage id="global.max" />
                </Text>
              )}
              <Text textStyle="screenStd" fontWeight="medium">
                <FormattedMessage id={`notification.criticality.${getWorstNotificationLevel(notification)}`} />
              </Text>
            </HStack>
          ) : (
            <HStack gap={1} flex={1}>
              <Icon as={IcAlert} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium">
                <FormattedMessage id="components.notification.noAlert" />
              </Text>
            </HStack>
          )}
        </HStack>
        <HStack width="100%" gap={3}>
          <HStack gap={1} flex={1}>
            <Icon as={getClearanceStatusIcon(clearance, dataInfoIconSize)} />
            <Text textStyle="screenStd" fontWeight="medium">
              <FormattedMessage id={`clearance.${clearance ?? 'UNKNOWN'}`} />
            </Text>
          </HStack>
          <HStack gap={1} flex={1}>
            <Icon as={IcFlightplan} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
            <Text textStyle="screenStd" fontWeight="medium">
              <FormattedMessage id="targetInfos.tab.information.withFlightPlan" values={{ hasFlightPlan }} />
            </Text>
          </HStack>
        </HStack>
        <HStack width="100%" gap={3}>
          <HStack gap={1} flex={1}>
            <Icon as={getReliabilityStatusIcon(reliability, dataInfoIconSize)} />
            <Text textStyle="screenStd" fontWeight="medium">
              <FormattedMessage id={`target.reliability.${reliability}`} />
            </Text>
          </HStack>
          {notification && (
            <HStack gap={1} flex={1}>
              <Icon as={IcEye} boxSize={{ base: '20px', '4k': '24px' }} color="neutral.500" />
              <Text textStyle="screenStd" fontWeight="medium">{`x${notification.occurrenceCount}`}</Text>
            </HStack>
          )}
        </HStack>
      </VStack>
    </PopupContainer>
  );
}
